import React, { useState, useEffect, useMemo } from 'react';
import './SubnauticaCreatureDirectory.scss';

const FILTER_CONFIG_SUBNAUTICA = {
  size: { label: 'Size', options: ['Tiny', 'Small', 'Medium', 'Large', 'Leviathan'] },
  behavior: { label: 'Behavior', options: ['Passive', 'Conditional', 'Aggressive', 'Ruthless'] },
  biome: { label: 'Biome', options: ['Shallows', 'Kelp Forest', 'Grassy Plateaus', 'Caves', 'Lost River', 'Containment Facility', 'Lava Zone', 'Islands', 'Multiple Biomes'] },
};

const SESSION_STORAGE_FILTERS_KEY_SUBNAUTICA = 'subnauticaCreaturesDirectoryFiltersV1';

const getDefaultFiltersSubnautica = () => {
  const defaults = {};
  for (const key in FILTER_CONFIG_SUBNAUTICA) {
    defaults[key] = [...FILTER_CONFIG_SUBNAUTICA[key].options];
  }
  return defaults;
};

const SubnauticaCreatureDirectory = ({ allCreatures, guessedCreatures, show, onClose, onCreatureSelect }) => {
  const [filters, setFilters] = useState(() => {
    try {
      const storedFilters = sessionStorage.getItem(SESSION_STORAGE_FILTERS_KEY_SUBNAUTICA);
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters);
        let isValid = true;
        for (const key in FILTER_CONFIG_SUBNAUTICA) {
          if (!parsedFilters[key] || !Array.isArray(parsedFilters[key])) {
            isValid = false;
            break;
          }
          parsedFilters[key] = parsedFilters[key].filter(opt => FILTER_CONFIG_SUBNAUTICA[key].options.includes(opt));
        }
        if (isValid) return parsedFilters;
      }
    } catch (error) {
      console.error("Error reading Subnautica creature filters from session storage:", error);
    }
    return getDefaultFiltersSubnautica();
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_FILTERS_KEY_SUBNAUTICA, JSON.stringify(filters));
    } catch (error) {
      console.error("Error saving Subnautica creature filters to session storage:", error);
    }
  }, [filters]);

  const handleFilterChange = (categoryKey, option, isRightClick = false) => {
    setFilters(prevFilters => {
      if (isRightClick) {
        // Right click: Turn off all options in this category except the clicked one
        // and ensure the clicked one is ON
        return {
          ...prevFilters,
          [categoryKey]: [option],
        };
      } else {
        // Left click: Toggle the clicked option on/off
        const currentCategorySelections = prevFilters[categoryKey] || [];
        const newCategorySelections = currentCategorySelections.includes(option)
          ? currentCategorySelections.filter(item => item !== option)
          : [...currentCategorySelections, option];
        
        return {
          ...prevFilters,
          [categoryKey]: newCategorySelections,
        };
      }
    });
  };

  const handleResetAllFilters = () => {
    setFilters(getDefaultFiltersSubnautica());
  };

  const filteredAndSortedCreatures = useMemo(() => {
    if (!allCreatures || allCreatures.length === 0) return [];

    return allCreatures
      .filter(creature => {
        for (const categoryKey in filters) {
          const selectedOptions = filters[categoryKey];

          if (FILTER_CONFIG_SUBNAUTICA[categoryKey] && FILTER_CONFIG_SUBNAUTICA[categoryKey].options.length > 0 && selectedOptions.length === 0) {
            return false; 
          }
          
          if (FILTER_CONFIG_SUBNAUTICA[categoryKey] && selectedOptions.length > 0) {
            let creatureMatchesCategory = false;
            const creatureValue = String(creature[categoryKey]);

            if (selectedOptions.includes(creatureValue)) {
                creatureMatchesCategory = true;
            }
            if (!creatureMatchesCategory) return false;
          }
        }
        return true;
      });
  }, [allCreatures, filters]);

  if (!show) {
    return null;
  }

  const handleCreatureClick = (creatureName) => {
    if (onCreatureSelect) {
      onCreatureSelect(creatureName);
    }
    onClose();
  };

  const areFiltersDefault = () => {
    const defaultFilters = getDefaultFiltersSubnautica();
    for (const categoryKey in filters) {
        if (!FILTER_CONFIG_SUBNAUTICA[categoryKey]) continue;
        const selectedForCategory = filters[categoryKey]?.sort().join(',') || '';
        const defaultForCategory = defaultFilters[categoryKey]?.sort().join(',') || '';
        if (selectedForCategory !== defaultForCategory) {
            return false;
        }
    }
    return true;
  };
  const isAnyFilterActive = !areFiltersDefault();

  return (
    <div className="subnautica-creature-directory__overlay" onClick={onClose}>
      <div className="subnautica-creature-directory__panel" onClick={(e) => e.stopPropagation()}>
        <button className="subnautica-creature-directory__close-button" onClick={onClose}>✕</button>
        <div className="subnautica-creature-directory__header">
            <h2>Creature Directory</h2>
            {isAnyFilterActive && (
                <button onClick={handleResetAllFilters} className="subnautica-creature-directory__reset-button">
                    Reset Filters
                </button>
            )}
        </div>

        <div className="subnautica-creature-directory__filters">
          {Object.entries(FILTER_CONFIG_SUBNAUTICA).map(([categoryKey, config]) => (
            <div key={categoryKey} className="subnautica-creature-directory__filter-category">
              <span className="subnautica-creature-directory__filter-label">{config.label}:</span>
              <div className="subnautica-creature-directory__filter-options">
                {config.options.map(option => (
                  <button
                    key={option}
                    className={`subnautica-creature-directory__filter-button ${
                      filters[categoryKey]?.includes(option) ? 'subnautica-creature-directory__filter-button--selected' : ''
                    }`}
                    onClick={() => handleFilterChange(categoryKey, option, false)}
                    onContextMenu={(e) => {
                      e.preventDefault(); // Prevent default context menu
                      handleFilterChange(categoryKey, option, true);
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <ul className="subnautica-creature-directory__list">
          {filteredAndSortedCreatures.length > 0 ? (
            filteredAndSortedCreatures.map((creature) => (
              <li
                key={creature.name} 
                className={`subnautica-creature-directory__list-item ${
                  guessedCreatures.includes(creature.name) ? 'subnautica-creature-directory__list-item--guessed' : ''
                }`}
                onClick={() => {
                  if (!guessedCreatures.includes(creature.name)) {
                    handleCreatureClick(creature.name);
                  }
                }}
                style={!guessedCreatures.includes(creature.name) ? { cursor: 'pointer' } : {}}
              >
                <div className="subnautica-creature-directory__item-content">
                  <span className="subnautica-creature-directory__item-name">{creature.name}</span>
                  <div className="subnautica-creature-directory__item-details">
                    <span className="subnautica-creature-directory__item-detail">{creature.size}</span>
                    <span className="subnautica-creature-directory__item-detail">{creature.behavior}</span>
                    <span className="subnautica-creature-directory__item-detail">{creature.biome}</span>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li className="subnautica-creature-directory__list-item subnautica-creature-directory__list-item--empty">No creatures match the current filters.</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default SubnauticaCreatureDirectory;